import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/currency";
import { Users, ChevronLeft, ChevronRight, ArrowRight, Car, Truck } from "lucide-react";
import DealCountdown from "@/components/DealCountdown";

const FALLBACK = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function FeaturedRentals() {
  const [cars, setCars] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    api.get("/cars", { params: { sort: "recommended" } })
      .then((r) => setCars(r.data.slice(0, 10)))
      .catch(() => {});
  }, []);

  const scroll = (dir) => {
    if (scroller.current) scroller.current.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (cars.length === 0) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-12 md:pt-16" data-testid="featured-rentals">
      <div className="flex items-end justify-between mb-6">
        <div>
          <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-cyan-400 flex items-center gap-2">
            <Car className="w-4 h-4" /> Wheels ready
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Drive Your Journey</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => scroll(-1)} data-testid="featured-rentals-prev" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 hover:text-cyan-400"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => scroll(1)} data-testid="featured-rentals-next" className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-300 hover:text-cyan-400"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>

      <div ref={scroller} className="flex gap-5 overflow-x-auto no-scrollbar pb-2 snap-x snap-mandatory">
        {cars.map((c) => {
          const img = c.image_url || c.images?.[0];
          return (
            <Link
              key={c.id}
              to={`/cars/${c.id}`}
              data-testid={`featured-rental-${c.id}`}
              className="group relative shrink-0 w-[300px] snap-start rounded-2xl overflow-hidden glass glass-hover"
            >
              <div className="relative aspect-[16/11] overflow-hidden bg-[#0B0E1B]">
                <img src={img ? fileUrl(img) : FALLBACK} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070A18] via-[#070A18]/20 to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border text-cyan-300 border-cyan-300/30">{c.car_type}</span>
                {c.deal_of_day && (
                  <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                    <span className="text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full bg-gradient-to-r from-[#ff5e00] to-[#d900ff] text-white font-bold shadow-lg" data-testid={`featured-deal-${c.id}`}>
                      Deal of the Day
                    </span>
                    <span className="glass rounded-full px-2.5 py-1">
                      <DealCountdown compact testid={`featured-countdown-${c.id}`} />
                    </span>
                  </div>
                )}
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                  <div>
                    <h3 className="font-heading font-extrabold text-white text-lg leading-tight">{c.name}</h3>
                    <span className="flex items-center gap-1.5 text-xs text-slate-300 mt-1">
                      <Users className="w-3.5 h-3.5 text-cyan-300" /> {c.passenger_capacity} passengers
                      {c.delivery_available && <><Truck className="w-3.5 h-3.5 text-emerald-400 ml-1" /> Delivery</>}
                    </span>
                  </div>
                  <div className="text-right">
                    {c.deal_of_day && <div className="text-slate-400 text-[11px] line-through">{formatPrice(c.original_price_per_day, c.currency)}</div>}
                    <span className="font-heading font-extrabold price-red text-2xl leading-none">{formatPrice(c.price_per_day, c.currency)}</span>
                    <div className="text-slate-400 text-[10px]">/ day</div>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
        <Link to="/cars" className="shrink-0 w-[160px] snap-start rounded-2xl glass glass-hover flex flex-col items-center justify-center gap-2 text-cyan-300">
          <ArrowRight className="w-6 h-6" />
          <span className="font-heading font-semibold text-sm">View all cars</span>
        </Link>
      </div>
    </section>
  );
}
